import { AnimatePresence, motion } from 'framer-motion'
import { lazy, Suspense, type ReactNode, useEffect, useRef, useState } from 'react'
import { ArrowUpRight } from '@phosphor-icons/react/dist/ssr/ArrowUpRight'
import { Check } from '@phosphor-icons/react/dist/ssr/Check'
import { CopySimple } from '@phosphor-icons/react/dist/ssr/CopySimple'
import { EditorialQuote } from '@/components/article/EditorialQuote'
import { ArticleProse, ProseParagraphs, renderRichText } from '@/components/article/ArticleProse'
import { FileDownloadCard } from '@/components/article/FileDownloadCard'
import { CaseStudyBookCallCta } from '@/components/case-study/CaseStudyBookCallCta'
import { CaseStudyClientLead } from '@/components/case-study/CaseStudyClientLead'
import { CaseStudyHeroBadges } from '@/components/case-study/CaseStudyHeroBadges'
import { CaseStudyContentSheet } from '@/components/case-study/CaseStudyContentSheet'
import { CaseStudyFaqAccordion } from '@/components/case-study/CaseStudyFaqAccordion'
import { CaseStudyHeroAside } from '@/components/case-study/CaseStudyHeroAside'
import { defaultCaseStudyTocItems } from '@/components/case-study/caseStudyDefaultToc'
import { HomeAiCaseStudies } from '@/components/home/HomeAiCaseStudies'
import portraitFront from '@/assets/portrait-gosha-front.avif'
import { SeoHead } from '@/components/seo/SeoHead'
import { siteConfig } from '@/config/site'
import { getAllCaseStudies, getAllNotes } from '@/data/contentService'
import {
  CASE_STUDY_SECTION_IDS,
  CASE_STUDY_SPECIAL_THANK_YOU_DEFAULT_HEADING,
} from '@/domain/caseStudySectionAnchors'
import type { CaseStudy } from '@/domain/content'
import type { CaseStudyLong, LongCaseStudyQuoteFields } from '@/domain/longCaseStudy'
import { copyContactEmail } from '@/lib/copyContactEmail'
import { filterCaseStudyTocItems } from '@/lib/caseStudyToc'
import { caseStudyCanonicalPath, caseStudySlugFromListUrl } from '@/lib/caseStudyPublicUrl'
import { resolveSyncedCaseStudyTags } from '@/lib/caseStudyTags'
import { ProjectVideo } from '../../design-gosha/site/src/ProjectVideo'
import { videoProjects } from '../../design-gosha/site/src/video-projects'

const MermaidBlock = lazy(() =>
  import('@/components/case-study/MermaidBlock').then((m) => ({ default: m.MermaidBlock }))
)
const CaseStudyTreeChart = lazy(() =>
  import('@/components/case-study/CaseStudyTreeChart').then((m) => ({ default: m.CaseStudyTreeChart }))
)

const CASE_STUDY_ABOUT_BIO =
  'I build AI-native workflows that turn context into useful sales, content, and publishing artifacts before work becomes manual production.'

/** Same treatment as the inline LinkedIn anchor in `HomePage` hero body. */
const CASE_STUDY_EXTERNAL_LINK_CLASSNAME =
  'shrink-0 self-start text-[var(--foreground-muted)] underline decoration-[var(--foreground-muted)] underline-offset-3 [transition-property:color,text-decoration-color] duration-150 [transition-timing-function:cubic-bezier(0.2,0,0,1)] hover:text-[var(--foreground)] hover:decoration-[var(--foreground)]'

const collaboratorLinkLabel = (url: string, explicit?: string) => {
  const label = explicit?.trim()
  if (label) {
    return label
  }
  return /linkedin\.com/i.test(url) ? 'LinkedIn' : 'Website'
}

const CASE_STUDY_THANK_YOU_LINK_CLASSNAME = `${CASE_STUDY_EXTERNAL_LINK_CLASSNAME} text-[13px]`

type SectionProps = {
  id: string
  title?: string
  /** When set, replaces the default section title styles (compact headings, etc.). */
  titleClassName?: string
  children: ReactNode
}

const caseStudyClientVoiceQuote = (q: LongCaseStudyQuoteFields | undefined | null) => {
  if (!q) {
    return null
  }
  const body = q.body?.trim()
  if (!body) {
    return null
  }
  return (
    <EditorialQuote
      body={body}
      attribution={q.attribution}
      name={q.name}
      role={q.role}
      company={q.company}
      photoUrl={q.photo_url}
    />
  )
}

const Section = ({ id, title, titleClassName, children }: SectionProps) => (
  <section id={id} className="scroll-mt-28 md:scroll-mt-20">
    {title ? (
      <h2
        className={
          titleClassName ??
          'ui-heading-balance mb-4 font-display text-[20px] font-normal leading-6 tracking-[-0.2px] text-[var(--foreground)]'
        }
      >
        {title}
      </h2>
    ) : null}
    {children}
  </section>
)

type Props = {
  data: CaseStudyLong
  pathname: string
}

export const CaseStudyLongPage = ({ data, pathname }: Props) => {
  const videoProject = videoProjects.find(project => project.path === `/blog/${data.page.slug}`)
  const privatePreview = data.visibility === 'private'
  const seoCanonicalPathname =
    !privatePreview && pathname.startsWith('/blog/') ? caseStudyCanonicalPath(data.page.slug) : undefined
  const readTimeLabel = data.page.read_time?.trim()
    ? `${data.page.read_time.trim()} read`
    : undefined
  const hero = data.hero
  const syncedHeroTags = resolveSyncedCaseStudyTags({
    primary: hero?.tags,
    fallback: data.tags,
    max: 4,
  })
  const tocEnabled = data.content_sheet?.enabled !== false
  const [aboutEmailCopied, setAboutEmailCopied] = useState(false)
  const [copyEmailToastVisible, setCopyEmailToastVisible] = useState(false)
  const aboutCopyTimerRef = useRef<number | null>(null)
  const copyToastResetTimerRef = useRef<number | null>(null)
  const authorName = data.author?.name?.trim() || siteConfig.authorName
  const authorRole = data.author?.role?.trim() || siteConfig.authorTitle
  const authorEmail = data.author?.email?.trim() || siteConfig.contactEmail
  const authorBioParagraphs = data.author?.bio_paragraphs?.filter((p) => p.trim().length > 0)
  const authorBio = authorBioParagraphs?.join(' ') || CASE_STUDY_ABOUT_BIO
  const authorPhotoSrc = data.author?.photo_url?.trim() || portraitFront

  useEffect(
    () => () => {
      if (aboutCopyTimerRef.current !== null) {
        window.clearTimeout(aboutCopyTimerRef.current)
      }
      if (copyToastResetTimerRef.current !== null) {
        window.clearTimeout(copyToastResetTimerRef.current)
      }
    },
    [],
  )

  const showCopyEmailToast = () => {
    setCopyEmailToastVisible(true)
    if (copyToastResetTimerRef.current !== null) {
      window.clearTimeout(copyToastResetTimerRef.current)
    }
    copyToastResetTimerRef.current = window.setTimeout(() => {
      setCopyEmailToastVisible(false)
      copyToastResetTimerRef.current = null
    }, 1800)
  }

  const handleAboutCopyEmail = async () => {
    const ok = await copyContactEmail(authorEmail)
    if (!ok) {
      return
    }
    setAboutEmailCopied(true)
    if (aboutCopyTimerRef.current !== null) {
      window.clearTimeout(aboutCopyTimerRef.current)
    }
    aboutCopyTimerRef.current = window.setTimeout(() => {
      setAboutEmailCopied(false)
      aboutCopyTimerRef.current = null
    }, 1800)
    showCopyEmailToast()
  }

  const legacyThankYou =
    data.author?.collaborators_heading || data.author?.collaborators_body
      ? {
          heading: data.author.collaborators_heading ?? CASE_STUDY_SPECIAL_THANK_YOU_DEFAULT_HEADING,
          body: data.author.collaborators_body,
        }
      : undefined

  const specialThankYouBlock =
    data.collaborators || legacyThankYou?.body
      ? {
          heading:
            data.collaborators?.heading ??
            legacyThankYou?.heading ??
            CASE_STUDY_SPECIAL_THANK_YOU_DEFAULT_HEADING,
          body: data.collaborators?.body?.trim() || legacyThankYou?.body,
          people: data.collaborators?.people,
        }
      : undefined

  const primaryClientVoice = caseStudyClientVoiceQuote(data.quote)
  const impactClientVoice = caseStudyClientVoiceQuote(data.impact_quote)
  const faqClientVoice = caseStudyClientVoiceQuote(data.faq_quote)

  const tocItemsRaw = data.content_sheet?.items?.length
    ? data.content_sheet.items.map((i) => ({
        label: i.label,
        anchor: i.anchor.startsWith('#') ? i.anchor : `#${i.anchor}`,
        depth: i.depth,
      }))
    : defaultCaseStudyTocItems
  const tocItems = filterCaseStudyTocItems(tocItemsRaw, data, specialThankYouBlock)
  const allCaseStudies = getAllCaseStudies()
  const allNotes = getAllNotes()
  const readAlsoCaseStudies = (data.read_also?.items ?? [])
    .map((item) => caseStudySlugFromListUrl(item.url))
    .filter((slug): slug is string => Boolean(slug))
    .map((slug) => allCaseStudies.find((cs) => cs.page.slug === slug))
    .filter((cs): cs is CaseStudy => Boolean(cs))
    .slice(0, 4)

  const tocAside = (
    <aside
      className={
        'mx-auto flex w-full min-w-0 max-w-[448px] shrink flex-col gap-8 py-3 lg:sticky lg:top-0 lg:mx-0 lg:max-w-[240px] lg:self-start ' +
      'lg:h-[100dvh] lg:gap-0 lg:overflow-hidden lg:pb-4 lg:pt-0'
      }
    >
      <div className="min-h-0 pt-4 lg:flex-1 lg:overflow-y-auto lg:overscroll-y-contain [scrollbar-width:thin]">
        {tocEnabled ? <CaseStudyContentSheet items={tocItems} /> : null}
      </div>
      <div className="hidden shrink-0 lg:block">
        <CaseStudyBookCallCta variant="inline" />
      </div>
    </aside>
  )

  const showHeroBadges = Boolean(syncedHeroTags.length)

  const caseStudyTitleHeader = (
    <header
      id={CASE_STUDY_SECTION_IDS.hero}
      className="w-full scroll-mt-28 md:scroll-mt-20"
    >
      <h1 className="ui-heading-balance font-display text-[28px] font-semibold leading-[1.06] tracking-[-0.56px] text-[var(--foreground)] [text-wrap:pretty]">
        {data.page.title}
      </h1>
      {showHeroBadges ? (
        <div className="mt-4">
          <CaseStudyHeroBadges
            tags={syncedHeroTags}
            readEstimate={readTimeLabel}
          />
        </div>
      ) : null}
    </header>
  )

  return (
    <>
      <SeoHead
        pathname={pathname}
        canonicalPathname={seoCanonicalPathname}
        kind={privatePreview ? 'private-follow-up' : 'case-study-long'}
        caseStudyLong={privatePreview ? undefined : data}
      />
      <div className="site-page mx-auto w-full min-w-0 max-w-[1104px] px-4 pb-32 md:px-6">
        <div
          className={
            'grid w-full grid-cols-1 gap-x-10 gap-y-6 ' +
            (hero
              ? 'lg:mx-auto lg:w-fit lg:grid-cols-[minmax(0,252px)_minmax(0,448px)_minmax(0,240px)] lg:gap-x-6 xl:gap-x-10'
              : 'lg:mx-auto lg:w-fit lg:grid-cols-[minmax(0,448px)_minmax(0,240px)] lg:gap-x-6 xl:gap-x-10')
          }
        >
          <div
            className={
              'space-y-4 lg:row-start-1 ' +
              (hero ? 'lg:col-start-2' : 'lg:col-span-2')
            }
          >
            <div className="mx-auto w-full min-w-0 max-w-[448px] lg:mx-0">
              {hero?.client ? (
                <CaseStudyClientLead client={hero.client} industry={hero.industry} />
              ) : null}
              {caseStudyTitleHeader}
            </div>
          </div>

          {hero ? (
            <div className="lg:col-start-1 lg:row-start-2">
              <CaseStudyHeroAside
                hero={hero}
                hideClient
                hideIndustry={Boolean(hero.client)}
                hideFocusArea={Boolean(hero.focus_area)}
              />
            </div>
          ) : null}

          <div
            className={
              'mx-auto w-full min-w-0 max-w-[448px] shrink lg:mx-0 ' +
              (hero ? 'lg:col-start-2 lg:row-start-2' : 'lg:col-start-1 lg:row-start-2')
            }
          >
            <div className="flex flex-col gap-14">
          {videoProject ? <ProjectVideo project={videoProject} /> : null}
          {data.tldr?.body ? (
              <Section id={CASE_STUDY_SECTION_IDS.tldr} title="TL;DR">
                <ProseParagraphs
                  paragraphs={data.tldr.body
                    .replace(/\r\n/g, '\n')
                    .split(/\n{2,}/)
                    .map((p) => p.trim())
                    .filter(Boolean)}
                />
              </Section>
          ) : null}

          {primaryClientVoice ? (
              <Section id={CASE_STUDY_SECTION_IDS.quote}>
                {primaryClientVoice}
              </Section>
          ) : null}

          {data.problem?.paragraphs?.length ? (
            <Section id={CASE_STUDY_SECTION_IDS.problem} title={data.problem.heading ?? 'The problem'}>
                <ProseParagraphs paragraphs={data.problem.paragraphs} />
              </Section>
          ) : null}

          {data.old_workflow?.steps?.length || data.old_workflow?.intro ? (
            <Section id={CASE_STUDY_SECTION_IDS.oldWorkflow} title={data.old_workflow.heading ?? 'Old workflow'}>
                {data.old_workflow.intro ? (
                  <ArticleProse>
                    <p>{renderRichText(data.old_workflow.intro)}</p>
                  </ArticleProse>
                ) : null}
                {data.old_workflow.steps?.length ? (
                  <ul className="mt-4 list-disc space-y-2 pl-5 text-[15px] leading-[22.5px] tracking-[-0.16px] text-[var(--foreground)] [text-wrap:pretty]">
                    {data.old_workflow.steps.map((s, i) => (
                      <li key={i}>{renderRichText(s)}</li>
                    ))}
                  </ul>
                ) : null}
              </Section>
          ) : null}

          {data.reframing?.paragraphs?.length ? (
            <Section id={CASE_STUDY_SECTION_IDS.reframing} title={data.reframing.heading ?? 'Reframing'}>
                <ProseParagraphs paragraphs={data.reframing.paragraphs} />
              </Section>
          ) : null}

          {data.solution?.paragraphs?.length ||
          (data.solution?.execution_steps && data.solution.execution_steps.length > 0) ? (
            <Section id={CASE_STUDY_SECTION_IDS.solution} title={data.solution?.heading ?? 'The solution'}>
                <ProseParagraphs paragraphs={data.solution?.paragraphs} />
                {data.solution?.execution_steps?.length ? (
                  <ol className="mt-6 list-decimal space-y-5 pl-5 text-[15px] leading-[22.5px] tracking-[-0.16px] text-[var(--foreground)] [text-wrap:pretty]">
                    {data.solution.execution_steps.map((step, i) => (
                      <li key={i}>
                        <span className="font-display font-semibold">{step.title}</span>
                        <p className="mt-1 font-normal leading-[22.5px] text-[var(--foreground-muted)]">{step.description}</p>
                      </li>
                    ))}
                  </ol>
                ) : null}
                {data.solution?.example ? (
                  <div className="mt-7 rounded-[16px] border border-[var(--foreground)] bg-[var(--foreground)] p-4">
                    {data.solution.example.label ? (
                      <p className="text-[11px] font-semibold uppercase leading-[16.5px] tracking-[0.275px] text-[#b8b8b8]">
                        {data.solution.example.label}
                      </p>
                    ) : null}
                    <div
                      className={`flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between ${data.solution.example.label ? 'mt-2' : ''}`}
                    >
                      {data.solution.example.description ? (
                        <p className="max-w-[210px] text-[15px] leading-[22.5px] tracking-[-0.16px] text-[var(--background)] [text-wrap:pretty]">
                          {data.solution.example.description}
                        </p>
                      ) : null}
                      <a
                        href={data.solution.example.url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex min-h-11 shrink-0 items-center justify-center gap-2 self-start rounded-[8px] bg-[var(--background)] px-4 py-2.5 text-[14px] font-semibold leading-5 text-[var(--foreground)] transition-opacity duration-150 hover:opacity-85 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--background)]"
                      >
                        {data.solution.example.cta_label}
                        <ArrowUpRight aria-hidden="true" size={16} weight="bold" />
                      </a>
                    </div>
                  </div>
                ) : null}
              </Section>
          ) : null}

          {data.workflow_schema?.body ? (
            <Section
                id={CASE_STUDY_SECTION_IDS.workflowSchema}
                title="Workflow schema"
              >
                {data.workflow_schema.type === 'mermaid' ||
                /flowchart|graph\s|sequenceDiagram|classDiagram/i.test(data.workflow_schema.body) ? (
                  <Suspense fallback={null}>
                    <MermaidBlock
                      chart={data.workflow_schema.body.replace(/^```mermaid\n?|\n?```$/g, '').trim()}
                    />
                  </Suspense>
                ) : (
                  <pre className="overflow-x-auto rounded-[16px] border border-[#e0e0e0] bg-[#fafafa] p-4 font-mono text-sm leading-6 text-[var(--foreground)]">
                    {data.workflow_schema.body}
                  </pre>
                )}
              </Section>
          ) : null}

          {data.custom_blocks?.items?.length ? (
            <Section id="custom-blocks" title="Visual breakdown">
              <div className="space-y-6">
                {data.custom_blocks.items.map((block, index) => (
                  <Suspense key={`${block.type}-${index}`} fallback={null}>
                    <CaseStudyTreeChart
                      title={block.title}
                      caption={block.caption}
                      data={block.data}
                    />
                  </Suspense>
                ))}
              </div>
            </Section>
          ) : null}

          {data.before_after?.rows?.length ? (
            <Section id={CASE_STUDY_SECTION_IDS.beforeAfter} title={data.before_after.heading ?? 'Before / after'}>
                <div className="min-w-0 w-full overflow-hidden rounded-[16px] border border-[#e0e0e0] bg-white text-[14px] leading-[19.25px]">
                  {/*
                    Grid: metric column fills remaining width (1fr). Before/After size to column max-content,
                    with min 0 so they can shrink on narrow viewports (extra wrap, no horizontal scroll).
                  */}
                  <div
                    className="grid w-full min-w-0 gap-0 text-left [grid-template-columns:minmax(112px,0.82fr)_minmax(0,1.1fr)_minmax(0,1.1fr)]"
                    role="table"
                    aria-label="Before and after metrics"
                  >
                    <div role="row" className="contents">
                      <div
                        role="columnheader"
                        className="border-b border-[#e0e0e0] bg-[#fafafa] px-3 py-2.5 text-[11px] font-semibold uppercase leading-[16.5px] tracking-[0.275px] text-[var(--foreground-muted)]"
                      >
                        Metric
                      </div>
                      <div
                        role="columnheader"
                        className="border-b border-[#e0e0e0] bg-[#fafafa] px-3 py-2.5 text-[11px] font-semibold uppercase leading-[16.5px] tracking-[0.275px] whitespace-nowrap text-[var(--foreground-muted)]"
                      >
                        Before
                      </div>
                      <div
                        role="columnheader"
                        className="border-b border-[#e0e0e0] bg-[#fafafa] px-3 py-2.5 text-[11px] font-semibold uppercase leading-[16.5px] tracking-[0.275px] whitespace-nowrap text-[var(--foreground-muted)]"
                      >
                        After
                      </div>
                    </div>
                    {(data.before_after?.rows ?? []).map((row, i, rows) => {
                      const isLastRow = i === rows.length - 1
                      const rowDivider = isLastRow
                        ? 'border-b-0'
                        : 'border-b border-[#e0e0e0]'
                      return (
                        <div key={i} role="row" className="contents">
                          <div
                            role="cell"
                            className={`min-w-0 px-3 py-2.5 align-top font-medium text-[var(--foreground)] break-words [overflow-wrap:anywhere] ${rowDivider}`}
                          >
                            {row.metric}
                          </div>
                          <div
                            role="cell"
                            className={`px-3 py-2.5 align-top break-words text-[var(--foreground-muted)] [overflow-wrap:anywhere] ${rowDivider}`}
                          >
                            {row.before}
                          </div>
                          <div
                            role="cell"
                            className={`px-3 py-2.5 align-top break-words text-[var(--foreground)] [overflow-wrap:anywhere] ${rowDivider}`}
                          >
                            {row.after}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </Section>
          ) : null}

          {data.impact?.paragraphs?.length ? (
            <Section id={CASE_STUDY_SECTION_IDS.impact} title={data.impact.heading ?? 'Impact'}>
                <ProseParagraphs paragraphs={data.impact.paragraphs} />
                {impactClientVoice ? (
                  <div
                    id={CASE_STUDY_SECTION_IDS.quoteImpact}
                    className="mt-10 scroll-mt-28 md:scroll-mt-20"
                  >
                    {impactClientVoice}
                  </div>
                ) : null}
              </Section>
          ) : null}

          {data.transferability?.paragraphs?.length ? (
            <Section
                id={CASE_STUDY_SECTION_IDS.transferability}
                title={data.transferability.heading ?? 'Transferability'}
              >
                <ProseParagraphs paragraphs={data.transferability.paragraphs} />
              </Section>
          ) : null}

          {data.gift?.body ? (
            <Section id={CASE_STUDY_SECTION_IDS.gift} title={data.gift.heading ?? 'Gift'}>
                <ArticleProse>
                  <p>{renderRichText(data.gift.body)}</p>
                </ArticleProse>
                {data.gift.cta_label && data.gift.cta_url ? (
                  <FileDownloadCard
                    title={data.gift.cta_label}
                    href={data.gift.cta_url}
                    fileLabel="PDF · workflow checklist"
                  />
                ) : null}
              </Section>
          ) : null}

          {faqClientVoice ? (
            <div
              id={CASE_STUDY_SECTION_IDS.quoteFaq}
              className="scroll-mt-28 md:scroll-mt-20"
            >
              {faqClientVoice}
            </div>
          ) : null}

          {data.faq?.items?.length ? (
            <Section
              id={CASE_STUDY_SECTION_IDS.faq}
              title="FAQ"
              titleClassName={
                faqClientVoice
                  ? 'ui-heading-balance mt-14 mb-4 font-display text-[20px] font-normal leading-6 tracking-[-0.2px] text-[var(--foreground)]'
                  : undefined
              }
            >
                <CaseStudyFaqAccordion items={data.faq.items} />
              </Section>
          ) : null}

          <Section id={CASE_STUDY_SECTION_IDS.author}>
              <div className="flex min-w-0 flex-col gap-5">
                <div className="flex items-start justify-between gap-3 sm:gap-4">
                  <div className="flex min-w-0 flex-1 items-start gap-3 sm:gap-4">
                    <div className="relative shrink-0">
                      <div className="overflow-hidden rounded-[5px] [box-shadow:0_1px_2px_rgba(0,0,0,0.05)]">
                        <img
                          src={authorPhotoSrc}
                          alt={`Portrait of ${authorName}`}
                          className="h-[42px] w-7 object-cover sm:h-[48px] sm:w-8"
                          width={32}
                          height={48}
                          loading="lazy"
                          decoding="async"
                        />
                      </div>
                      <span
                        className="absolute right-0 bottom-0 size-2 translate-x-px translate-y-px rounded-full bg-[#22c55e] ring-2 ring-[var(--background)]"
                        aria-hidden
                      />
                    </div>
                    <div className="min-w-0 space-y-0.5 pt-px">
                      <div className="font-display text-lg font-semibold leading-tight tracking-tight text-[var(--foreground)]">
                        {authorName}
                      </div>
                      <div className="text-sm leading-snug text-[var(--foreground-muted)]">{authorRole}</div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleAboutCopyEmail}
                    aria-label="Copy email address"
                    className="group inline-flex shrink-0 items-center gap-1 self-start font-medium text-[var(--foreground)] underline decoration-[#b7b7b7] underline-offset-3 [transition-property:text-decoration-color] duration-150 [transition-timing-function:cubic-bezier(0.2,0,0,1)] hover:decoration-[var(--foreground)]"
                  >
                    email
                    <span className="relative inline-flex size-5 items-center justify-center" aria-hidden>
                      <motion.span
                        animate={{
                          opacity: aboutEmailCopied ? 0 : 1,
                          scale: aboutEmailCopied ? 0.25 : 1,
                          filter: aboutEmailCopied ? 'blur(4px)' : 'blur(0px)',
                        }}
                        transition={{ type: 'spring', duration: 0.3, bounce: 0 }}
                        className="absolute inset-0 flex items-center justify-center"
                      >
                        <span className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-150 group-hover:opacity-100">
                          <CopySimple size={16} weight="fill" />
                        </span>
                        <span className="absolute inset-0 flex items-center justify-center opacity-100 transition-opacity duration-150 group-hover:opacity-0">
                          <CopySimple size={16} weight="regular" />
                        </span>
                      </motion.span>
                      <motion.span
                        animate={{
                          opacity: aboutEmailCopied ? 1 : 0,
                          scale: aboutEmailCopied ? 1 : 0.25,
                          filter: aboutEmailCopied ? 'blur(0px)' : 'blur(4px)',
                        }}
                        transition={{ type: 'spring', duration: 0.3, bounce: 0 }}
                        className="absolute inset-0 flex items-center justify-center"
                      >
                        <Check size={16} weight="bold" />
                      </motion.span>
                    </span>
                  </button>
                </div>
                <p className="text-[15px] leading-[22.5px] tracking-[-0.16px] text-[var(--foreground)] [text-wrap:pretty]">
                  {authorBio}
                </p>
              </div>
            </Section>

          {specialThankYouBlock?.people?.length ? (
            <Section
                id={CASE_STUDY_SECTION_IDS.specialThankYou}
                title={specialThankYouBlock.heading ?? CASE_STUDY_SPECIAL_THANK_YOU_DEFAULT_HEADING}
              >
                {specialThankYouBlock.people?.length ? (
                  <div className="flex min-w-0 flex-col gap-4">
                    {specialThankYouBlock.people.map((person, i) => (
                      <div
                        key={i}
                        className="flex items-start justify-between gap-3 sm:gap-4"
                      >
                        <div className="min-w-0 flex-1 space-y-px pt-px">
                          <div className="font-display text-[15px] font-semibold leading-snug tracking-[-0.02em] text-[var(--foreground)]">
                            {person.name}
                          </div>
                          {person.role ? (
                            <div className="text-[13px] leading-snug text-[var(--foreground-muted)]">
                              {person.role}
                            </div>
                          ) : null}
                        </div>
                        {person.link_url ? (
                          <a
                            href={person.link_url}
                            rel="noreferrer"
                            target="_blank"
                            className={CASE_STUDY_THANK_YOU_LINK_CLASSNAME}
                            aria-label={`${collaboratorLinkLabel(person.link_url, person.link_label)} (opens in new tab)`}
                          >
                            {collaboratorLinkLabel(person.link_url, person.link_label)}
                          </a>
                        ) : null}
                      </div>
                    ))}
                  </div>
                ) : null}
              </Section>
          ) : null}

          {readAlsoCaseStudies.length ? (
              <HomeAiCaseStudies
                title="Read Also"
                items={readAlsoCaseStudies}
                notes={allNotes.slice(0, 2)}
                showExploreMore
                padded={false}
                variant="readAlso"
              />
          ) : null}
            </div>
          </div>

          <div
            className={
              (hero ? 'lg:col-start-3 lg:row-start-2' : 'lg:col-start-2 lg:row-start-2') +
              ' hidden min-w-0 lg:block'
            }
          >
            {tocAside}
          </div>
        </div>
      </div>
      <CaseStudyBookCallCta variant="fixed" />
      <AnimatePresence>
        {copyEmailToastVisible ? (
          <motion.div
            key="case-study-email-copied-toast"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ type: 'spring', duration: 0.3, bounce: 0 }}
            className="pointer-events-none fixed inset-x-0 z-[60] flex justify-center px-4 max-md:bottom-[calc(4rem+60px+env(safe-area-inset-bottom,0px))] md:bottom-8"
            role="status"
            aria-live="polite"
          >
            <div className="rounded-[var(--radius-lg)] bg-[var(--primary)] px-3 py-2 text-[13px] font-medium tracking-[-0.13px] text-[var(--primary-foreground)] shadow-[0_8px_24px_rgba(0,0,0,0.28)]">
              Email copied
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </>
  )
}
